import scrollToTopDomUtil from 'client/utils/dom/scroll-to-top'
import ComponentLoader from 'components/ComponentLoader'
import { View } from 'components/View'
import Announcement from 'containers/App/components/Announcement'
import LocationListener from 'containers/App/components/LocationListener'
import Header from 'containers/Header'
import { SubNavBar } from 'containers/SubNavBar'
import openSuccessBannerAction from 'modules/banner/actions/open-success'
import { NETWORK_STATUS } from 'modules/network/constants'
import getStatusNetworkSelect from 'modules/network/selectors'
import { isEditingRoutesSelector } from 'modules/routes/selectors/is-editing'
import hasGymaholicHeaderRouteUtil from 'modules/routes/utils/has-gymaholic-header'
import hasSubNavBarRouteUtil from 'modules/routes/utils/has-sub-nav-bar'
import { hasSideBarRouteUtil } from 'modules/routes/utils'
import {
  STYLES_FONT_SIZE_PX,
  STYLES_LAZY_LOAD_DEFAULT_ELEMENT_HEIGHT
} from 'modules/styles/constants/index.cjs'
import fetchIsConnectedTrainingNetworkAction from 'modules/training/modules/network/actions/fetch-is-connected'
import isSmallScreenViewportSelect from 'modules/viewport/selectors/is-small-screen'
import isDesktopFromWebpackOrFromSSRUtil from 'modules/viewport/utils/is-desktop-screen-from-webpack-or-from-ssr'
import PropTypes from 'prop-types'
import React from 'react'
import LazyLoad from 'react-lazyload'
import 'styles/no-scrollbars-list.css'
import isAppRouteUtil from 'modules/routes/utils/is-app'
import hasMenuSelect from 'modules/menu/selectors/has-menu'
import hasBannerSelect from 'modules/banner/selectors/has-banner'
import hasLocationModalSelect from 'modules/modal/selectors/has-location-modal'
import useDispatch from 'modules/component/utils/hooks/useDispatch.js'
import useSelector from 'modules/component/utils/hooks/useSelector.js'
import { usePreviousHookUtil } from 'modules/component/utils/hooks/usePrevious.js'
import ErrorBoundary from 'containers/ErrorBoundary'
import WithRouteAndMatch from 'components/WithRouteAndMatch'
import OnboardingModal from 'containers/OnboardingModal/index.js'
import initQueryParametersAppAction from 'modules/app/actions/init-query-parameters/index.js'
import { isBotUserAgentUtil } from 'modules/user-agent/utils/is-bot/index.js'
import queueMicrotaskPerfUtil from 'modules/performance/queue-microtask/index.js'
import handleLocationUpdateAppAction from 'containers/App/redux/actions/handle-location-update/index.js'
import shouldHideHeaderRouterSelect from 'modules/router/selectors/should-hide-header/index.js'
import shouldHideSubNavbarRouterSelect from 'modules/router/selectors/should-hide-sub-navbar/index.js'
import { HandleNewCustomerHeaderBanner } from 'containers/HandleNewCustomerHeaderBanner/index.js'
import NotFound from 'components/NotFound/index.js'
import { shouldRenderNotFoundPageSelect } from 'modules/training/modules/app/selectors/shouldRenderNotFoundPage/index.js'
import { shouldShowSidebarSelect } from 'modules/app/selectors/shouldShowSidebar/index.js'
import { ConnectedConsentBanner } from 'modules/consent/components/ConsentBanner'

function loadBanner () {
  return import(/* webpackChunkName: "modules/banner/containers" */ 'modules/banner/containers')
}

function loadNavBar () {
  return import(/* webpackChunkName: "pages/App/NavBar" */ 'pages/App/NavBar')
}

function loadMenu () {
  return import(/* webpackChunkName: "modules/menu/containers/Menu" */ 'modules/menu/containers/Menu')
}

function loadModal () {
  return import(/* webpackChunkName: "containers/Modal" */ 'containers/Modal')
}

// function loadFooter () { return import(/* webpackChunkName:
// "containers/NavBar/Footer" */ 'containers/NavBar/Footer') }

function loadSidebar () {
  return import(/* webpackChunkName: "containers/Sidebar" */ 'containers/Sidebar')
}

function loadFooter () {
  return import(/* webpackChunkName: "containers/Footer" */ 'containers/Footer')
}

function AppComponent (props) {
  const {
    location,
    match,
    route,
    children
  } = props
  // const intl = useIntl()
  const isSmallScreen = useSelector(isSmallScreenViewportSelect)
  // const debugProps = {
  //   props,
  //   intl,
  //   isSmallScreen
  // }
  // logUtil('AppComponent').debug('debugProps=', debugProps)

  const dispatch = useDispatch()
  const isEditing = useSelector(state => isEditingRoutesSelector()(state, props))
  const networkStatus = useSelector(getStatusNetworkSelect)
  const shouldHideHeader = useSelector(shouldHideHeaderRouterSelect)
  const shouldRenderNotFoundPage = useSelector(shouldRenderNotFoundPageSelect)
  const shouldHideSubNavbar = useSelector(shouldHideSubNavbarRouterSelect)
  const prevNetworkStatus = usePreviousHookUtil(networkStatus)
  const prevLocation = usePreviousHookUtil(location)
  const isDifferentLocation = prevLocation && prevLocation !== location
  const hasBanner = useSelector(hasBannerSelect)
  const hasModal = useSelector(hasLocationModalSelect)
  const hasMenu = useSelector(hasMenuSelect)
  const shouldShowSidebar = useSelector(shouldShowSidebarSelect)

  const handleTrainingLoadDataOnRouteChange = (prevLocation) => {
    if (
      (!prevLocation || !isAppRouteUtil(prevLocation?.pathname)) &&
      isAppRouteUtil(location?.pathname)
    ) {
      dispatch(
        fetchIsConnectedTrainingNetworkAction()
      )
    }
  }

  const showNetworkBanner = (status) => {
    if (status === NETWORK_STATUS.ONLINE) {
      dispatch(
        openSuccessBannerAction('Online')
      )
      return
    }

    dispatch(openSuccessBannerAction('Offline'))
  }

  const handleMount = () => {
    dispatch(
      initQueryParametersAppAction({ location })
    )

    handleTrainingLoadDataOnRouteChange()
    if (!isBotUserAgentUtil()) {
      import('./index.scss')
    }
  }

  React.useEffect(() => {
    handleMount()
  }, [])

  React.useEffect(() => {
    if (
      prevNetworkStatus &&
      prevNetworkStatus !== networkStatus
    ) {
      showNetworkBanner(networkStatus)
    }
  }, [
    networkStatus,
    prevNetworkStatus
  ])

  React.useEffect(() => {
    dispatch(
      handleLocationUpdateAppAction({
        isDifferentLocation,
        isEditing,
        location,
        prevLocation,
        scrollToTop,
        handleTrainingLoadDataOnRouteChange
      })
    )
  }, [
    location,
    prevLocation
  ])

  const scrollToTop = async (animate = false) => {
    if (BROWSER) {
      queueMicrotaskPerfUtil(() => {
        scrollToTopDomUtil(animate)
      })
    }
  }

  const renderAnnouncement = () => {
    if (shouldRenderNotFoundPage) {
      return
    }

    return (
      <Announcement
        location={location}
      />
    )
  }

  const renderBanner = () => {
    if (!hasBanner) {
      return
    }

    return (
      <ComponentLoader
        loadComponent={loadBanner}
        shouldNotShowLoader
      />
    )
  }

  const renderHeader = () => {
    if (shouldHideHeader) {
      return
    }

    return (
      <Header
        isEditing={isEditing}
        isSmallScreen={isSmallScreen}
        location={location}
        match={match}
        route={route}
      />
    )
  }

  const renderSubNavBar = () => {
    if (shouldHideSubNavbar || isEditing) {
      return
    }
    return (
      <SubNavBar
        location={location}
        match={match}
        route={route}
      />
    )
  }

  const renderTrainingNavBar = () => {
    const { route } = props
    if (!route.isApp || shouldHideSubNavbar) {
      return
    }
    return (
      <ComponentLoader
        loadComponent={loadNavBar}
        location={location}
        match={match}
        route={route}
        shouldNotShowLoader
      />
    )
  }

  const renderLoader = () => {
    return (
      <div
        className='app-loader'
        style={{
          height: '0.27em',
          position: 'fixed',
          overflow: 'hidden',
          top: '0',
          left: '0',
          right: '0',
          zIndex: '200'
        }}
      />
    )
  }

  const renderMainContent = () => {
    if (shouldRenderNotFoundPage) {
      return <NotFound />
    }

    return children
  }

  const renderContent = () => {
    let paddingTop
    if (isEditing) {
      paddingTop = '4.44em'
    } else if (
      hasSubNavBarRouteUtil(getMatchPath()) ||
      !hasGymaholicHeaderRouteUtil(getMatchPath())
    ) {
      paddingTop = 0
    } else if (isSmallScreen) {
      paddingTop = '2.77em'
    } else {
      paddingTop = '4.44em'
    }

    return (
      <>
        <View
          style={{
            flexDirection: isSmallScreen ? View.DIRECTION.COLUMN : View.DIRECTION.ROW,
            paddingTop
          }}
        >
          <View
            style={{
              overflowX: 'hidden',
              overflowY: 'hidden',
              flexGrow: '1'
            }}
          >
            {renderAnnouncement()}
            {renderMainContent()}
          </View>
          {renderSideBar()}
        </View>
        {renderFooter()}
      </>
    )
  }

  const renderMenu = () => {
    if (!hasMenu) {
      return
    }
    return (
      <ComponentLoader
        loadComponent={loadMenu}
        location={location}
        match={match}
        route={route}
        shouldNotShowLoader
      />
    )
  }

  const renderModal = () => {
    if (!hasModal) {
      return
    }
    return (
      <ComponentLoader
        loadComponent={loadModal}
        location={location}
        match={match}
        route={route}
        shouldNotShowLoader
      />
    )
  }

  // renderNavBarFooter () {
  //   if (isEditing || !isSmallScreen) {
  //     return
  //   }
  //
  //   if (SMALL_SCREEN || (SERVER && global.SMALL_SCREEN)) {
  //     const NavbarFooter = require('containers/NavBar/Footer').default
  //     return <NavbarFooter />
  //   } else if (isSmallScreen) {
  //     return (
  //       <ComponentLoader
  //         loadComponent={loadFooter}
  //         shouldNotShowLoader
  //       />
  //     )
  //   }
  // }

  const getMatchPath = () => {
    return match?.pathname
  }

  const renderSideBar = () => {
    if (!shouldShowSidebar) {
      return
    }

    const isDesktopSSR = isDesktopFromWebpackOrFromSSRUtil()
    if (isDesktopSSR && hasSideBarRouteUtil(getMatchPath())) {
      const Sidebar = require('containers/Sidebar').default
      return (
        <Sidebar
          location={location}
          match={match}
          route={route}
        />
      )
    } else if (!isDesktopSSR && isSmallScreen) {
      return (
        <LazyLoad
          height={STYLES_LAZY_LOAD_DEFAULT_ELEMENT_HEIGHT}
          offset={-1}
        >
          <ComponentLoader
            loadComponent={loadSidebar}
            location={location}
            match={match}
            route={route}
            shouldNotShowLoader
          />
        </LazyLoad>
      )
    }
  }

  const renderFooter = () => {
    return (
      <LazyLoad
        height={STYLES_LAZY_LOAD_DEFAULT_ELEMENT_HEIGHT}
        offset={-1}
      >
        <ComponentLoader
          loadComponent={loadFooter}
          shouldNotShowLoader
          hasNewsletter={
            isDesktopFromWebpackOrFromSSRUtil() &&
            !hasSideBarRouteUtil(getMatchPath())
          }
        />
      </LazyLoad>
    )
  }

  return (
    <ErrorBoundary>
      <View
        style={{
          position: 'relative',
          width: '100%',
          fontWeight: '400',
          fontSize: isSmallScreen
            ? `${STYLES_FONT_SIZE_PX.SMALL}px`
            : `${STYLES_FONT_SIZE_PX.NORMAL}px`,
          WebkitFontSmoothing: 'antialiased',
          overflowX: 'hidden',
          wordBreak: 'break-word'
        }}
      >
        {renderBanner()}
        {renderLoader()}
        {renderHeader()}
        {renderSubNavBar()}
        {renderTrainingNavBar()}
        {renderContent()}
        {renderModal()}
        {renderMenu()}
        <LocationListener />
        <OnboardingModal />
        <HandleNewCustomerHeaderBanner />
        <ConnectedConsentBanner />
        {/* <LocationPrompt /> */}
      </View>
    </ErrorBoundary>
  )
}

AppComponent.propTypes = {
  children: PropTypes.element.isRequired,
  location: PropTypes.object.isRequired,
  match: PropTypes.object,
  route: PropTypes.object.isRequired,
  shouldRenderNotFoundPage: PropTypes.bool
}

const App = WithRouteAndMatch(AppComponent)

export default App
